import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import { ActiveProjectProvider } from "@/contexts/ActiveProjectContext";
import { ProcessingQueueProvider } from "@/contexts/ProcessingQueueContext";
import { Loader2 } from "lucide-react";

// Eagerly import landing page for fastest FCP/LCP
import Index from "./pages/Index";

// Lazy-load all other pages so their CSS splits into separate chunks
const Auth = lazy(() => import("./pages/Auth"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const OnboardingLens = lazy(() => import("./pages/OnboardingLens"));

const Projects = lazy(() => import("./pages/Projects"));
const ProjectView = lazy(() => import("./pages/ProjectView"));
const TranscriptLibrary = lazy(() => import("./pages/TranscriptLibrary"));
const Settings = lazy(() => import("./pages/Settings"));
const ResearchAdvisor = lazy(() => import("./pages/ResearchAdvisor"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const Inbox = lazy(() => import("./pages/Inbox"));

const Findings = lazy(() => import("./pages/Findings"));
const Draft = lazy(() => import("./pages/Draft"));
const WhatIsVerithos = lazy(() => import("./pages/WhatIsVerithos"));
const SampleReport = lazy(() => import("./pages/SampleReport"));
const HowToCite = lazy(() => import("./pages/HowToCite"));
const VCardPage = lazy(() => import("./pages/VCardPage"));
const VBoardPage = lazy(() => import("./pages/VBoardPage"));
const VerithosVsLLMs = lazy(() => import("./pages/VerithosVsLLMs"));
const QdasComparison = lazy(() => import("./pages/QdasComparison"));
const Whitepaper = lazy(() => import("./pages/Whitepaper"));
const Resources = lazy(() => import("./pages/Resources"));
const NotFound = lazy(() => import("./pages/NotFound"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Contact = lazy(() => import("./pages/Contact"));
const Feedback = lazy(() => import("./pages/Feedback"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));

import { ScrollToTop } from "./components/ScrollToTop";

// Minimal full-page loader for lazy routes
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

// Query client with optimized defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Reduce unnecessary refetches - data stays fresh for 2 minutes
      staleTime: 2 * 60 * 1000,
      // Keep unused data in cache for 5 minutes
      gcTime: 5 * 60 * 1000,
      // Don't refetch on window focus for better UX
      refetchOnWindowFocus: false,
      // Retry failed queries once
      retry: 1,
      // Don't refetch when component remounts if data is fresh
      refetchOnMount: false,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <SubscriptionProvider>
          <ActiveProjectProvider>
            <ProcessingQueueProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <ScrollToTop />
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                      <Route path="/" element={<Index />} />
                      <Route path="/auth" element={<Auth />} />
                      <Route path="/onboarding" element={<Onboarding />} />
                      <Route path="/onboarding/start" element={<OnboardingLens />} />
                      {/* /projects is now the default landing after login */}
                      <Route path="/projects" element={<Projects />} />
                      {/* /dashboard redirects to /projects for backwards compatibility */}
                      <Route path="/dashboard" element={<Navigate to="/projects" replace />} />
                      <Route path="/project/:slug" element={<ProjectView />} />
                      <Route path="/transcripts" element={<TranscriptLibrary />} />
                      <Route path="/codebook" element={<Navigate to="/findings?tab=findings" replace />} />
                      <Route path="/findings" element={<Findings />} />
                      <Route path="/draft" element={<Draft />} />
                      <Route path="/settings" element={<Settings />} />
                      <Route path="/inbox" element={<Inbox />} />
                      <Route path="/research-advisor" element={<ResearchAdvisor />} />
                      <Route path="/pricing" element={<Pricing />} />
                      <Route path="/privacy-policy" element={<Privacy />} />
                      <Route path="/terms-of-service" element={<Terms />} />
                      <Route path="/what-is-verithos" element={<WhatIsVerithos />} />
                      <Route path="/sample-report" element={<SampleReport />} />
                      <Route path="/how-to-cite" element={<HowToCite />} />
                      <Route path="/checkout/success" element={<CheckoutSuccess />} />
                      <Route path="/admin" element={<AdminDashboard />} />
                      {/* Resources hub */}
                      <Route path="/resources" element={<Resources />} />
                      <Route path="/faq" element={<FAQ />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/feedback" element={<Feedback />} />
                      {/* Verithos vs LLMs guide */}
                      <Route path="/verithos-vs-llms" element={<VerithosVsLLMs />} />
                      <Route path="/verithos-vs-chatgpt" element={<Navigate to="/verithos-vs-llms" replace />} />
                      <Route path="/verithos-vs-nvivo-atlasti-maxqda-dedoose" element={<QdasComparison />} />
                      {/* Whitepaper microsite */}
                      <Route path="/whitepaper" element={<Whitepaper />} />
                      {/* Redirect old sub-routes to main whitepaper */}
                      <Route path="/whitepaper/workflow" element={<Navigate to="/whitepaper#workflow" replace />} />
                      <Route path="/whitepaper/standards" element={<Navigate to="/whitepaper#standards" replace />} />
                      <Route path="/whitepaper/references" element={<Navigate to="/whitepaper#references" replace />} />
                      <Route path="/whitepaper/changelog" element={<Navigate to="/whitepaper#references" replace />} />
                      {/* V Sharing Routes (Public) */}
                      <Route path="/v/card/:token" element={<VCardPage />} />
                      <Route path="/v/board/:token" element={<VBoardPage />} />
                      <Route path="/unsubscribe" element={<Unsubscribe />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                </Suspense>
              </BrowserRouter>
            </ProcessingQueueProvider>
          </ActiveProjectProvider>
        </SubscriptionProvider>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
